import { useCallback, useEffect, useRef } from 'react';
import type { Message, ProductAction, ProductCard, RichContent } from '../types/messages';
import type { ChatAction } from '../types/state';
import type { CommerceCartMode, SessionConfig } from '../types/chat';
import type { WidgetStrings } from '../i18n/types';
import type { MerchantPreset } from '../merchants/types';
import { CART_DISCOUNT_MENU_PREFIX, CART_PRODUCT_HELP_PREFIX } from '../merchants/cartHelp';
import { getSessionId } from '../utils/session';
import { threadCapabilityHeaders } from '../utils/threadCapability';
import {
  CART_CHECKOUT_NO_PREFIX,
  CART_CHECKOUT_STARTED_PREFIX,
  CART_KEEP_SHOPPING_ACTION,
} from '../commerce/cartActionValues';
import { buildCartErrorMessage } from '../commerce/cartErrorMessage';
import type { CheckoutUrlStore, ProductActionResult } from '../commerce/shopifyCartFlow';

type CommerceModule = typeof import('../commerce/lazy');
let loadedCommerce: CommerceModule | undefined;
const loadCommerce = () =>
  import(/* webpackChunkName: "commerce" */ '../commerce/lazy').then(module => {
    loadedCommerce = module;
    return module;
  });

export type ProductActionPayload = { action: ProductAction; product: ProductCard };

export interface LocalMessagePayload {
  text: string;
  type?: Message['type'];
  content?: RichContent;
  localOnly?: boolean;
}

export interface UseCommerceActionsOptions {
  chatDispatch: React.Dispatch<ChatAction>;
  messages: Message[];
  trackAnalytics: (action: string, data?: Record<string, unknown>) => void;
  trackCommerceOutcome: (event: unknown) => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  closeChat: () => void;
  merchantPreset: MerchantPreset;
  strings: WidgetStrings;
  cartMode: CommerceCartMode | null | undefined;
  cartEndpoint?: string | null;
  cartAttributionEndpoint?: string | null;
  apiBaseUrl?: string | null;
  messageEndpoint?: string | null;
  apiKey?: string | null;
  widgetAuthToken?: string | null;
  agentId?: string | null;
  agentOrgId?: string | null;
  threadId?: string | null;
  withAuthHeaders: () => Record<string, string>;
  sessionConfig?: SessionConfig;
  onProductAction?: (payload: ProductActionPayload) => void;
  onBeforeCartOpen?: () => void;
  addLocalMessage: (
    sender: Message['sender'],
    payload: LocalMessagePayload,
    status: Message['status']
  ) => void;
  /** Sends a plain text button value to the agent. */
  handleButtonClick: (value: string) => Promise<void>;
}

export interface UseCommerceActionsResult {
  handleProductAction: (payload: ProductActionPayload) => Promise<void>;
  /** Handles cart confirmation button values; resolves `true` when the value was one of them. */
  handleCartConfirmationAction: (value: string) => Promise<boolean>;
}

/**
 * Wires the React-free Shopify cart flow (`commerce/shopifyCartFlow`) to chat state:
 * product card actions, the cart confirmation follow-up buttons, and automatic add-to-cart
 * directives carried on assistant messages.
 */
export function useCommerceActions({
  chatDispatch,
  messages,
  trackAnalytics,
  trackCommerceOutcome,
  scrollToBottom,
  closeChat,
  merchantPreset,
  strings,
  cartMode,
  cartEndpoint,
  cartAttributionEndpoint,
  apiBaseUrl,
  messageEndpoint,
  apiKey,
  widgetAuthToken,
  agentId,
  agentOrgId,
  threadId,
  withAuthHeaders,
  sessionConfig,
  onProductAction,
  onBeforeCartOpen,
  addLocalMessage,
  handleButtonClick,
}: UseCommerceActionsOptions): UseCommerceActionsResult {
  const checkoutUrlRef = useRef('');
  const checkoutUrlStoreRef = useRef<CheckoutUrlStore>({
    get: () => checkoutUrlRef.current,
    set: url => {
      checkoutUrlRef.current = url;
    },
  });
  const processedAutoActionsRef = useRef<Set<string>>(new Set());
  const pendingCartActionsRef = useRef(new Map<string, Promise<void>>());
  const cartOperationSequenceRef = useRef(0);

  const applyResult = useCallback(
    (result: ProductActionResult) => {
      const added =
        result.kind === 'added'
          ? result.confirmation
          : result.kind === 'error'
            ? [result.message]
            : [];
      if (added.length === 0) return;
      for (const message of added) {
        chatDispatch({ type: 'ADD_MESSAGE', message });
      }
      setTimeout(() => scrollToBottom('smooth'), 0);
    },
    [chatDispatch, scrollToBottom]
  );

  const handleProductAction = useCallback(
    ({ action, product }: ProductActionPayload) => {
      const cartKey =
        action.type === 'add_to_cart'
          ? JSON.stringify([
              product.id,
              action.value || product.selectedVariantId || action.url || product.url,
            ])
          : null;
      const pending = cartKey && pendingCartActionsRef.current.get(cartKey);
      if (pending) return pending;
      const win = typeof window !== 'undefined' ? window : null;
      const startedAt = Date.now();
      const operationId = `${startedAt}-${++cartOperationSequenceRef.current}`;
      let status = 'failed';
      const recordCartOperation = (phase: 'started' | 'completed') => {
        if (!cartKey) return;
        try {
          trackAnalytics(`cart_operation_${phase}`, {
            important: true,
            operationId,
            productId: product.id,
            viewportWidth: win?.innerWidth,
            mobile: win?.matchMedia?.(
              '(max-width: 640px), (max-width: 1024px) and (max-height: 640px) and (pointer: coarse)'
            ).matches,
            ...(phase === 'completed'
              ? { status, durationMs: Math.max(0, Date.now() - startedAt) }
              : {}),
          });
        } catch {
          // Observability must never prevent a cart mutation or release of its lock.
        }
      };
      recordCartOperation('started');
      const request = loadCommerce()
        .then(commerce =>
          commerce.runProductAction(
            {
              product,
              action,
              cartMode,
              cartEndpoint,
              cartAttributionEndpoint,
              apiBaseUrl,
              messageEndpoint,
              authToken: widgetAuthToken || apiKey,
              threadId,
              agentId,
              orgId: agentOrgId,
              merchantCart: merchantPreset.cart,
              strings,
            },
            {
              window: win,
              authHeaders: withAuthHeaders,
              threadHeaders: id => threadCapabilityHeaders(id, sessionConfig),
              trackAnalytics,
              trackOutcome: trackCommerceOutcome,
              onProductAction,
              onBeforeCartOpen,
              navigation: commerce.createBrowserNavigation(win),
              checkoutUrlStore: checkoutUrlStoreRef.current,
              getConversionId: () => commerce.getConversionId(),
              getSessionId: () => getSessionId(sessionConfig),
              getPageAttribution: () => commerce.getPageAttribution(),
              now: () => new Date(),
            }
          )
        )
        .then(result => {
          status =
            result.kind === 'added'
              ? 'succeeded'
              : result.kind === 'error'
                ? 'failed'
                : result.kind;
          applyResult(result);
        })
        .catch(error => {
          if (action.type === 'add_to_cart') {
            applyResult({
              kind: 'error',
              reason: String(error),
              message: buildCartErrorMessage({
                product,
                productUrl: action.url || product.url,
                strings,
                now: new Date(),
              }),
            });
          }
          trackAnalytics('product_action_error', { action: action.type, productId: product.id });
        })
        .finally(() => {
          if (cartKey) pendingCartActionsRef.current.delete(cartKey);
          recordCartOperation('completed');
        });
      if (cartKey) pendingCartActionsRef.current.set(cartKey, request);
      return request;
    },
    [
      trackAnalytics,
      onProductAction,
      onBeforeCartOpen,
      cartEndpoint,
      cartAttributionEndpoint,
      apiBaseUrl,
      messageEndpoint,
      threadId,
      withAuthHeaders,
      sessionConfig,
      cartMode,
      trackCommerceOutcome,
      agentId,
      agentOrgId,
      widgetAuthToken,
      apiKey,
      merchantPreset,
      strings,
      applyResult,
    ]
  );

  const handleCartConfirmationAction = useCallback(
    async (value: string): Promise<boolean> => {
      if (
        !value.startsWith(CART_CHECKOUT_STARTED_PREFIX) &&
        !value.startsWith(CART_CHECKOUT_NO_PREFIX) &&
        value !== CART_KEEP_SHOPPING_ACTION &&
        !value.startsWith(CART_DISCOUNT_MENU_PREFIX) &&
        !value.startsWith(CART_PRODUCT_HELP_PREFIX)
      )
        return false;
      // Product actions already load commerce. Keep their follow-up UI synchronous
      // once ready, while still loading on demand for host-provided cart buttons.
      const { runCartConfirmationAction } = loadedCommerce ?? (await loadCommerce());
      return runCartConfirmationAction(value, {
        strings,
        addLocalMessage,
        cartMode,
        handleButtonClick,
        merchantPreset,
        trackAnalytics,
        trackCommerceOutcome,
        closeChat,
      });
    },
    [
      strings,
      addLocalMessage,
      cartMode,
      handleButtonClick,
      merchantPreset,
      trackAnalytics,
      trackCommerceOutcome,
      closeChat,
    ]
  );

  // Automatic add-to-cart directives: run each once per browser, only while unexpired.
  useEffect(() => {
    for (const message of messages) {
      const autoAction = message.richContent?.autoAction;
      if (message.sender !== 'ai' || !autoAction || autoAction.type !== 'add_to_cart') continue;
      if (processedAutoActionsRef.current.has(autoAction.id)) continue;

      const expiresAt = Date.parse(autoAction.expiresAt);
      if (!Number.isFinite(expiresAt) || expiresAt < Date.now()) {
        processedAutoActionsRef.current.add(autoAction.id);
        continue;
      }

      const storageKey = `rw-auto-action:${autoAction.id}`;
      try {
        if (window.localStorage.getItem(storageKey)) {
          processedAutoActionsRef.current.add(autoAction.id);
          continue;
        }
      } catch {
        // In-memory deduplication still protects this widget instance.
      }

      const product = message.richContent?.products?.find(
        candidate => String(candidate.id) === autoAction.productId
      );
      const action = product?.actions?.find(candidate => candidate.type === 'add_to_cart');
      if (
        !product ||
        !action ||
        product.variantSelectionRequired === true ||
        (!action.value && !action.url && !product.selectedVariantId)
      ) {
        processedAutoActionsRef.current.add(autoAction.id);
        continue;
      }

      processedAutoActionsRef.current.add(autoAction.id);
      try {
        window.localStorage.setItem(storageKey, new Date().toISOString());
      } catch {
        // The action can proceed when storage is unavailable.
      }
      handleProductAction({ action, product });
    }
  }, [messages, handleProductAction]);

  return { handleProductAction, handleCartConfirmationAction };
}
